import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getHomepageSectionML } from '../services/api';
import { Building2, Globe, TrendingUp, Users, ArrowRight, CheckCircle, Sparkles, Palette, Type, FileText, Laptop, Monitor, Server, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

const iconMap = { 'building': Building2, 'globe': Globe, 'trending-up': TrendingUp, 'users': Users };

const ServicesPage = () => {
  const { language } = useLanguage();
  const [content, setContent] = useState(null);

  useEffect(() => {
    const load = async () => {
      const lang = ['pt','en','fr','ar','zh'].includes(language) ? language : 'en';
      const r = await getHomepageSectionML('services', lang);
      if (r.success) setContent(r.data);
    };
    load();
  }, [language]);

  const detailedServices = [
    { title: 'Business Architecture', features: ['Company formation in UAE Free Zones', 'Corporate governance structuring', 'Compliance & regulatory advisory', 'Strategic planning & execution'], color: '#023E8A' },
    { title: 'International Trading', features: ['Commodity sourcing & procurement', 'Cross-border logistics management', 'Trade finance facilitation', 'Supplier due diligence'], color: '#00B4D8' },
    { title: 'Project Management', features: ['Infrastructure project coordination', 'Multi-stakeholder management', 'Budget & timeline optimization', 'Quality assurance & delivery'], color: '#FF6B35' },
    { title: 'Strategic Consulting', features: ['Market entry strategy', 'Partnership development', 'M&A advisory', 'Digital transformation'], color: '#48CAE4' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero — extends behind the transparent fixed header on this route */}
      <section className="bg-[#023E8A] text-white pt-44 pb-24">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-10 bg-[#48CAE4]" />
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#48CAE4]">Our Services</p>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.1] tracking-tight mb-6">
              {content?.heading || 'Integrated Business Solutions'}
            </h1>
            <p className="text-lg text-white/80 font-light leading-relaxed max-w-2xl">
              {content?.subheading || 'Premium services for global operations across emerging and developed markets.'}
            </p>
          </div>
        </div>
      </section>

      {/* CMS Services Grid */}
      <section className="py-24 bg-[#f8f9fa]">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {content?.services?.filter(s => s.visible).sort((a, b) => a.order - b.order).map((service, i) => {
              const Icon = iconMap[service.icon] || Building2;
              return (
                <div key={i} className="bg-white rounded-2xl p-8 border border-gray-100 hover:shadow-xl hover:-translate-y-2 transition-all duration-500 group">
                  <div className="bg-[#023E8A]/5 w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#FF6B35] transition-colors">
                    <Icon className="w-7 h-7 text-[#023E8A] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-lg font-bold text-[#2B2D42] mb-3 group-hover:text-[#FF6B35] transition-colors">{service.title}</h3>
                  <p className="text-sm text-[#6C757D] leading-relaxed">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Detailed Services */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00B4D8] mb-4">What We Deliver</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#2B2D42] tracking-tight">Comprehensive Service Portfolio</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {detailedServices.map((svc, i) => (
              <div key={i} className="rounded-2xl p-8 border border-gray-100 hover:shadow-lg transition-all">
                <div className="w-2 h-12 rounded-full mb-6" style={{ backgroundColor: svc.color }} />
                <h3 className="text-xl font-extrabold text-[#2B2D42] mb-4">{svc.title}</h3>
                <ul className="space-y-3">
                  {svc.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#00B4D8] flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-[#6C757D]">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BMP Brand Studio — Featured premium service */}
      <section className="relative py-24 bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] overflow-hidden" data-testid="services-brand-studio-feature">
        {/* Decorative grid + glow */}
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-[#FF6B35]/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-[#00B4D8]/15 rounded-full blur-3xl" />

        <div className="container mx-auto px-6 relative">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            {/* Left: copy */}
            <div className="lg:col-span-3">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-300/15 border border-amber-300/30 text-amber-300 text-[11px] font-bold uppercase tracking-[0.2em] mb-6">
                <Sparkles className="w-3 h-3" /> New · Limited cohort 2026
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] tracking-tight mb-6">
                BMP <span className="bg-gradient-to-r from-[#FF6B35] to-amber-300 bg-clip-text text-transparent">Brand Studio</span>
              </h2>
              <p className="text-lg text-white/70 leading-relaxed font-light mb-8 max-w-xl">
                A complete corporate-identity studio built into our platform. AI-assisted, human-curated. From the first
                sketch to a live, branded website — in days, not months.
              </p>

              {/* 4 pillars */}
              <div className="grid grid-cols-2 gap-3 mb-8">
                {[
                  { icon: Palette, label: 'Palette & logo system', sub: '3 logo variants + colour tokens' },
                  { icon: Type, label: 'Typography & voice', sub: 'Google Fonts pairing + brand voice' },
                  { icon: FileText, label: 'Brand Book PDF', sub: 'Institutional 6-section document' },
                  { icon: Globe, label: 'One-page website', sub: 'Live URL + admin handover' },
                ].map((p) => (
                  <div key={p.label} className="flex items-start gap-3 p-3 rounded-xl bg-white/5 border border-white/10 hover:border-[#FF6B35]/50 transition">
                    <p.icon className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-bold text-white">{p.label}</div>
                      <div className="text-[11px] text-white/50">{p.sub}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Link to="/brand-studio">
                  <button className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-[#FF6B35] to-amber-400 text-[#0F172A] font-extrabold tracking-tight shadow-xl hover:shadow-2xl hover:scale-[1.03] transition-all" data-testid="services-brand-studio-cta">
                    Discover Brand Studio
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </Link>
                <Link to="/schedule" className="text-sm text-white/70 hover:text-white underline underline-offset-4">
                  Or book a discovery call
                </Link>
              </div>
            </div>

            {/* Right: pricing card */}
            <div className="lg:col-span-2">
              <div className="relative rounded-2xl bg-gradient-to-br from-white to-slate-100 p-6 shadow-2xl border border-white/40 overflow-hidden">
                <div className="absolute top-3 right-3 text-[10px] font-bold uppercase tracking-widest bg-[#0F172A] text-amber-300 px-2 py-1 rounded">Best value</div>
                <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#FF6B35] mb-1">Professional plan</div>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-4xl font-extrabold text-[#0F172A]">AED 549</span>
                  <span className="text-sm text-gray-500 line-through">AED 990</span>
                </div>
                <ul className="space-y-2 text-sm text-[#0F172A]/80 mb-6">
                  {[
                    'Master brand prompt + onboarding wizard',
                    '3 AI logo variants + 3 social posts',
                    'Brand Book PDF (institutional 6 sections)',
                    'Business card + letterhead mockups',
                    'One-page website handover + admin login',
                    'Engagement tracking included',
                  ].map((l) => (
                    <li key={l} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-[#0F172A] flex-shrink-0 mt-0.5" />
                      <span>{l}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/brand-studio">
                  <button className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-[#0F172A] text-white font-bold hover:bg-[#1E293B] transition">
                    Compare all plans <ArrowRight className="w-4 h-4" />
                  </button>
                </Link>
                <div className="mt-3 text-[11px] text-gray-500 text-center">
                  Also available — Starter from AED 289 · Premium from AED 1,465
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BMP IT Solutions — multi-brand institutional procurement */}
      <section className="relative py-24 bg-white overflow-hidden" data-testid="services-it-solutions-feature">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: 'linear-gradient(135deg, #023E8A 25%, transparent 25%), linear-gradient(225deg, #023E8A 25%, transparent 25%)', backgroundSize: '40px 40px' }} />
        <div className="container mx-auto px-6 relative">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            {/* Left: copy */}
            <div className="lg:col-span-3">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00B4D8]/10 border border-[#00B4D8]/30 text-[#0F172A] text-[11px] font-bold uppercase tracking-[0.2em] mb-6">
                <Sparkles className="w-3 h-3 text-[#00B4D8]" /> New · IT Procurement Desk
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#0F172A] leading-[1.05] tracking-tight mb-6">
                BMP <span className="bg-gradient-to-r from-[#023E8A] to-[#00B4D8] bg-clip-text text-transparent">IT Solutions</span>
              </h2>
              <p className="text-lg text-[#6C757D] leading-relaxed font-light mb-8 max-w-xl">
                Multi-brand institutional IT procurement — Lenovo, HP, Dell, Microsoft and Cisco —
                consolidated under a single point of contact. From the first quote to deployed
                devices, fully managed by our desk.
              </p>

              {/* 4 pillars */}
              <div className="grid grid-cols-2 gap-3 mb-8">
                {[
                  { icon: Laptop, label: 'Business Laptops', sub: 'ThinkPad · EliteBook · Latitude' },
                  { icon: Server, label: 'Workstations', sub: 'ISV-certified for CAD & engineering' },
                  { icon: Monitor, label: '5K Ultrawide Monitors', sub: 'Thunderbolt 4 · KVM · executive desks' },
                  { icon: ShieldCheck, label: 'Microsoft 365 + Security', sub: 'Intune · Defender · Conditional Access' },
                ].map((p) => (
                  <div key={p.label} className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100 hover:border-[#00B4D8] transition">
                    <p.icon className="w-5 h-5 text-[#00B4D8] flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-sm font-bold text-[#0F172A]">{p.label}</div>
                      <div className="text-[11px] text-[#6C757D]">{p.sub}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Link to="/it-solutions">
                  <button
                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#0F172A] text-white font-extrabold tracking-tight shadow-xl hover:bg-[#1E293B] hover:scale-[1.03] transition-all"
                    data-testid="services-it-solutions-cta"
                  >
                    Explore IT catalogue
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </Link>
                <Link to="/contact" className="text-sm text-[#6C757D] hover:text-[#0F172A] underline underline-offset-4">
                  Or talk to a specialist
                </Link>
              </div>
            </div>

            {/* Right: brands stack */}
            <div className="lg:col-span-2">
              <div className="rounded-2xl bg-gradient-to-br from-[#0F172A] to-[#1E293B] p-6 shadow-2xl">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-300 mb-4">Authorised institutional channel</div>
                <div className="grid grid-cols-3 gap-2">
                  {['Lenovo', 'HP', 'Dell', 'Apple', 'Microsoft', 'Cisco'].map((b) => (
                    <div key={b} className="px-3 py-3 rounded-xl bg-white/5 border border-white/10 text-center text-white text-sm font-extrabold tracking-tight hover:border-amber-300 hover:bg-white/10 transition">
                      {b}
                    </div>
                  ))}
                </div>
                <div className="mt-5 pt-4 border-t border-white/10 text-[11px] text-white/60 leading-relaxed">
                  <div className="flex items-start gap-2 mb-1.5"><CheckCircle className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" /> Consolidated multi-brand procurement</div>
                  <div className="flex items-start gap-2 mb-1.5"><CheckCircle className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" /> Multi-currency AED/USD with frozen FX rates</div>
                  <div className="flex items-start gap-2"><CheckCircle className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" /> Free Zone delivery · UAE + Africa</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#023E8A] py-16">
        <div className="container mx-auto px-6 text-center">
          <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-4">Need a Tailored Solution?</h3>
          <p className="text-white/70 mb-8 max-w-lg mx-auto">Every business is unique. Let us design the right approach for your goals.</p>
          <Link to="/contact">
            <button className="bg-[#FF6B35] hover:bg-[#E5562D] text-white font-bold px-10 py-4 rounded-xl text-lg transition-all hover:scale-105 inline-flex items-center gap-2">
              Request a Quote <ArrowRight className="w-5 h-5" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
